import { PropertyValueMap, TemplateResult, html, nothing, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { SlotController } from '../../controllers/slot-controller';
import { BlocksCancelEvent, BlocksCloseEvent } from '../../events';
import { BlocksOpenEvent } from '../../events/blocks-open';
import { customConverter } from '../../util/custom-converter';
import { KEYS } from '../../util/event-keys';
import { BlocksElement } from '../BlocksElement';
import '../button/button';
import '../heading/heading';
import '../icon/icons/close';
import type { InlineNotificationType } from './inline-notification.model';
import styles from './inline-notification.styles';

/**
 * @slot before - The component before content
 * @slot default - The component content
 * @slot after - The component after content
 * @fires bl-inline-notification-open - emitted when component is opened
 * @fires bl-inline-notification-close - emitted when component is closed
 * @fires bl-inline-notification-cancel - emitted when cancel icon is clicked
 * @csspart base - The component's base wrapper.
 * @csspart content - The component's content wrapper.
 * @csspart text - The component's default text content
 * @csspart icon - The component's icon wrapper.
 * @csspart part-before - The component's before slot.
 * @csspart body - The component's body wrapper.
 * @csspart title - The component's title wrapper.
 * @csspart part-default - The component's default slot.
 * @csspart after-wrapper - The component's after slot wrapper.
 * @csspart part-after - The component's after slot.
 * @csspart button - The component's close button.
 * @csspart button-after - The component's `<cat-icon-close>`.
 */
export class BlocksInlineNotification extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Text for the inline-notification heading
   */
  @property({ type: String })
  heading: string | undefined;

  /**
   * Dismissible property
   * 1) Adds the ability to close when toggled to true
   */
  @property({ type: Boolean })
  dismissible: boolean | undefined;

  /**
   * Dismissed property
   * 1) State that changes to true and is removed when the inline-notification is dismissed
   */
  @property({ type: Boolean })
  isDismissed: boolean | undefined;

  /**
   * Style variants
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**success** renders a inline-notification that represents a success state</li>
   * <li>**warning** renders a inline-notification that represents a warning state</li>
   * <li>**error** renders a inline-notification that represents an error state</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  variant: InlineNotificationType;

  /**
   * @default true , controls whether focus should return to the trigger element once the notification closes
   */
  @property({
    // Allow "true|false" attribute values but keep the property boolean
    type: Boolean,
    converter: customConverter()
  })
  returnFocusToTrigger = true;

  /**
   * The ARIA live region politeness level for the notification.
   *
   * @type {string}
   * @default "assertive"
   */
  @property({ type: String })
  arialive: 'off' | 'polite' | 'assertive' = 'assertive';

  private previouslyActiveElement?: HTMLElement | null;

  protected slots = new SlotController(this, 'after', 'before', null);

  /**
   * Disconnected callback lifecycle
   */
  disconnectedCallback() {
    super.disconnectedCallback();
  }

  protected updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (_changedProperties.has('isDismissed') && !this.isDismissed) {
      this.previouslyActiveElement = document.activeElement as HTMLElement;
      this.dispatch({ eventName: 'bl-inline-notification-open', detailObj: { isActive: true } }) as BlocksOpenEvent;
    }
  }

  /**
   * On inline-notification dismiss
   * 1) Function that toggles isDismissed to true and removes the inline-notification from the UI and focus 
    backs the trigger if any.
   */
  public close() {
    if (this.emitCancelEvent()) {
      return;
    }
    this.isDismissed = true; /* 1 */
    if (this.returnFocusToTrigger && this.previouslyActiveElement) {
      (this.previouslyActiveElement as HTMLElement)?.focus();
    }
    this.dispatch({ eventName: 'bl-inline-notification-close', detailObj: { close: true } }) as BlocksCloseEvent;
  }

  /**
   * Keydown event
   * 1) If escape key is struck when the inline-notification is dismissible and is active, dismiss it
   */
  private handleKeyDown(e: KeyboardEvent) {
    if (this.dismissible && !this.isDismissed) {
      if (e.key === KEYS.ESCAPE) {
        this.close(); /* 1 */
      }
    }
  }

  /**
   * @fires cancel If we call preventDefault on cancel event object to not close inline-notification.
   * @returns boolean
   */
  private emitCancelEvent(): boolean {
    const cancelEvent = this.dispatch({
      eventName: 'bl-inline-notification-cancel',
      detailObj: {},
      optionsObj: { cancelable: true }
    }) as BlocksCancelEvent;

    return cancelEvent.defaultPrevented;
  }

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-inline-notification', {
      'cat-c-inline-notification--success': this.variant === 'success',
      'cat-c-inline-notification--warning': this.variant === 'warning',
      'cat-c-inline-notification--error': this.variant === 'error',
      'cat-is-dismissible': this.dismissible === true,
      'cat-c-inline-notification--has-slot-before': this.slots.hasSlotted('before'),
      'cat-c-inline-notification--has-slot-after': this.slots.hasSlotted('after')
    });

    return this.isDismissed
      ? html`${nothing}`
      : html`
          <div part="base" role="alert" aria-live=${this.arialive} class="${componentClassNames}" @keydown=${this.handleKeyDown}>
            <div part="content" class="cat-c-inline-notification__content">
              <div part="icon" class="cat-c-inline-notification__icon">
                <slot part="part-before" name="before"></slot>
              </div>
              <div part="body" class="cat-c-inline-notification__body">
                ${this.heading ? html` <div part="title" class="cat-c-inline-notification__title">${this.heading}</div> ` : html`${nothing}`}
                <div part="text"><slot part="part-default"></slot></div>
              </div>
            </div>

            <div part="after-wrapper" class="cat-c-inline-notification__after">
              <slot part="part-after" name="after"></slot>
            </div>

            ${this.dismissible
              ? html`
                  <cat-button
                    part="button"
                    exportparts=${this.getExportParts('button')}
                    class="cat-c-inline-notification__close-button"
                    variant="ghost"
                    ?hideText=${true}
                    @click=${this.close}
                    >Close
                    <cat-icon-close part="button-after" exportparts=${this.getExportParts('close', 'icon')} size="sm" slot="after"></cat-icon-close
                  ></cat-button>
                `
              : html`${nothing}`}
          </div>
        `;
  }
}

if (customElements.get('cat-inline-notification') === undefined) {
  customElements.define('cat-inline-notification', BlocksInlineNotification);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-inline-notification': BlocksInlineNotification;
  }
}
